/**
 * Booking helpers shared across the authed Scheduling/Compose flows and the
 * public /book/* pages: timezone-aware slot formatting and the email-safe
 * "suggested times" HTML block inserted into the Compose editor.
 */

/**
 * Public-facing version of an appointment/booking name. Host-side names may
 * carry placeholder tokens — {name}, {invitee}, {person}, {first} (with inner
 * spaces allowed, e.g. "{ name }") — that get substituted with the invitee's
 * name on the actual calendar invite. The invitee themselves should just see
 * "you", so we replace those tokens before display.
 */
export function displayTitle(name: string): string {
    return name.replace(/\{\s*(name|invitee|person|first)\s*\}/gi, "you");
}

/** Label for a meeting-link button. */
export function joinLabel(meetUrl: string): string {
    return meetUrl ? "Join video call" : "Join call";
}

/** Human name for the video provider behind a meeting link. */
export function meetProvider(meetUrl: string): string {
    let host = "";
    try { host = new URL(meetUrl).hostname; } catch { return "Video call"; }
    if (host.endsWith("kardoe.com")) return "Kardoe Meet";
    if (host.includes("zoom.")) return "Zoom";
    if (host === "meet.google.com") return "Google Meet";
    if (host.includes("teams.")) return "Microsoft Teams";
    if (host.includes("whereby")) return "Whereby";
    return host.replace(/^www\./, "");
}

/**
 * Room id out of whatever a guest pastes into the join box: a bare id, a short
 * link (meet.kardoe.com/v/<id>), with or without query/hash junk. Not validated
 * here — an unknown id fails at the join call.
 */
export function roomIdFromInput(raw: string): string {
    const [path] = raw.trim().split(/[?#]/);
    return path.replace(/\/+$/, "").split("/").pop() ?? "";
}

/** The invitee's IANA timezone, best-effort, with a sane fallback. */
export function detectTimeZone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
    } catch {
        return "America/New_York";
    }
}

/** "Mon, Jun 22 · 9:00 AM" — a slot start in the given zone. */
export function formatSlot(iso: string, timeZone: string): string {
    return new Intl.DateTimeFormat(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZone,
    }).format(new Date(iso));
}

/** "9:00 AM" — just the time, for grouped-by-day slot grids. */
export function formatTime(iso: string, timeZone: string): string {
    return new Intl.DateTimeFormat(undefined, {
        hour: "numeric",
        minute: "2-digit",
        timeZone,
    }).format(new Date(iso));
}

/** "Monday, June 22, 2026" — a day heading. */
export function formatDayHeading(iso: string, timeZone: string): string {
    return new Intl.DateTimeFormat(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
        timeZone,
    }).format(new Date(iso));
}

/** Calendar date key ("2026-06-22") for a slot in the given zone — used to
 *  bucket slots into days and to drive the public day picker. */
export function dayKey(iso: string, timeZone: string): string {
    // en-CA gives ISO-ish YYYY-MM-DD; zone-correct via the formatter.
    return new Intl.DateTimeFormat("en-CA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        timeZone,
    }).format(new Date(iso));
}

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

/** Friendly US-region label for an IANA timezone ("Eastern", "Pacific", …),
 *  used to qualify the suggested-times heading so the invitee knows which zone
 *  the listed times are in. Falls back to the short timezone name (e.g.
 *  "GMT+1") and finally the raw IANA string. */
export function friendlyTimeZone(timeZone: string): string {
    const region = US_REGION_BY_ZONE[timeZone];
    if (region) return region;
    try {
        const name = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" })
            .formatToParts(new Date())
            .find(part => part.type === "timeZoneName")?.value;
        if (name) return name;
    } catch {
        // fall through to the raw IANA string
    }
    return timeZone;
}

const US_REGION_BY_ZONE: Record<string, string> = {
    "America/New_York": "Eastern",
    "America/Detroit": "Eastern",
    "America/Toronto": "Eastern",
    "America/Chicago": "Central",
    "America/Denver": "Mountain",
    "America/Phoenix": "Mountain",
    "America/Los_Angeles": "Pacific",
    "America/Vancouver": "Pacific",
    "America/Anchorage": "Alaska",
    "Pacific/Honolulu": "Hawaii",
};

/** How the suggested-times block renders in the email. */
export type SuggestedTimesStyle = "buttons" | "links" | "text" | "widget";

/** Default footer lead-in (the "See more times" link sits after this). */
export const DEFAULT_SUGGESTED_TIMES_FOOTER = "None of these work?";
/** Default label for the trailing booking-page link. */
export const DEFAULT_SUGGESTED_TIMES_MORE = "See more times";

/**
 * A bulletproof, email-client-safe HTML block for a set of suggested times.
 * Tables + inline styles only (no flex) so it survives Outlook/Gmail. Each
 * slot links to the public suggested-booking page with the chosen start
 * preselected via `?slot=`.
 *
 * `style` controls the chrome:
 *   - "text" (default): a native bulleted list that inherits the editor's
 *     paragraph styling — a plain lead-in line, a real <ul> with one
 *     hyperlink per <li>, and a plain trailing line. No inline font overrides,
 *     so it reads as ordinary email body text.
 *   - "buttons": compact bordered buttons that flow in a row and wrap
 *     naturally — several per line, not full-width stacked.
 *   - "links": plain blue underlined text hyperlinks, one per line, at the
 *     smaller 14px caption size.
 *   - "widget": the same buttons, nested in a padded card with a tinted
 *     background so the block reads as a helpful inset, not body text.
 */
export function buildSuggestedTimesHtml(opts: {
    bookingUrlBase: string;
    token: string;
    slots: string[];
    timeZone: string;
    /** Appointment type slug — drives the "See more times" link to the full
     *  public booking page. Falls back to the suggested-set page if absent. */
    slug?: string;
    heading?: string;
    /** Lead-in before the "more times" link. Empty string omits it. */
    footer?: string;
    /** Label for the booking-page link. Empty string omits the link. */
    moreLabel?: string;
    style?: SuggestedTimesStyle;
    /** Omit the heading entirely. The editor NodeView uses this to render the
     *  slots beneath its own editable heading region without duplicating it;
     *  the sent email always includes the heading. */
    omitHeading?: boolean;
    /** Same idea as omitHeading, for the editor's editable footer line. */
    omitFooter?: boolean;
}): string {
    const { bookingUrlBase, token, slots, timeZone, slug, omitHeading, omitFooter } = opts;
    // An explicit caller-supplied heading is used verbatim; the default folds in
    // the friendly zone, e.g. "Suggested times (all Eastern)" (the "text" style
    // appends its own ":", so no trailing colon here).
    const heading = opts.heading ?? `Suggested times (all ${friendlyTimeZone(timeZone)})`;
    const footer = opts.footer ?? DEFAULT_SUGGESTED_TIMES_FOOTER;
    const moreLabel = opts.moreLabel ?? DEFAULT_SUGGESTED_TIMES_MORE;
    const style = opts.style ?? "text";
    const isWidget = style === "widget";
    const base = bookingUrlBase.replace(/\/+$/, "");

    const slotHref = (startIso: string) =>
        `${base}/book/suggested/${encodeURIComponent(token)}?slot=${encodeURIComponent(startIso)}`;
    // "See more times" goes to the type's full public booking page (every
    // available day/time), not the few suggested slots. Fall back to the
    // suggested-set page if no slug is available so it never breaks.
    const moreHref = slug
        ? `${base}/book/${encodeURIComponent(slug)}`
        : `${base}/book/suggested/${encodeURIComponent(token)}`;

    const footerInner = (linkStyle?: string) => {
        const lead = footer.trim();
        const more = moreLabel.trim();
        if (!lead && !more) return "";
        const link = more
            ? `<a href="${escapeHtml(moreHref)}"${linkStyle ? ` style="${linkStyle}"` : ""}>${escapeHtml(more)}</a>.`
            : "";
        return [lead ? escapeHtml(lead) : "", link].filter(Boolean).join(" ");
    };

    // "text" is a native bulleted list with no inline styling — it inherits the
    // editor's paragraph font and link styling, so it edits/reads like ordinary
    // body text rather than an email-client-safe table widget.
    if (style === "text") {
        const items = slots.map(startIso => {
            const label = escapeHtml(formatSlot(startIso, timeZone));
            return `<li><a href="${escapeHtml(slotHref(startIso))}">${label}</a></li>`;
        }).join("");
        const trail = omitFooter ? "" : (() => {
            const inner = footerInner();
            return inner ? `<p>${inner}</p>` : "";
        })();
        return (omitHeading ? "" : `<p>${escapeHtml(heading)}:</p>`)
            + `<ul>${items}</ul>`
            + trail;
    }

    // The slot block is itself a one-cell row in the outer table; its inner
    // markup differs by style but both stay table-based + inline-styled.
    let slotsRow: string;
    if (style === "links") {
        // One underlined hyperlink per line at the smaller 14px caption size.
        const links = slots.map(startIso => {
            const label = escapeHtml(formatSlot(startIso, timeZone));
            return `<tr><td style="padding:3px 0;font-family:Arial,Helvetica,sans-serif;`
                + `font-size:14px;line-height:1.4;">`
                + `<a href="${escapeHtml(slotHref(startIso))}" `
                + `style="color:#2563eb;text-decoration:underline;">${label}</a>`
                + `</td></tr>`;
        }).join("");
        slotsRow = `<tr><td style="padding:0;">`
            + `<table role="presentation" cellpadding="0" cellspacing="0" border="0" `
            + `style="border-collapse:collapse;">${links}</table>`
            + `</td></tr>`;
    } else {
        // Compact inline-block buttons that wrap. "widget" reuses this so the
        // card is chrome around the same one-click times.
        const buttons = slots.map(startIso => {
            const label = escapeHtml(formatSlot(startIso, timeZone));
            return `<a href="${escapeHtml(slotHref(startIso))}" `
                + `style="display:inline-block;margin:0 6px 6px 0;padding:8px 14px;`
                + `background-color:#2563eb;color:#ffffff;text-decoration:none;`
                + `border-radius:6px;border:1px solid #2563eb;`
                + `font-family:Arial,Helvetica,sans-serif;font-size:14px;`
                + `font-weight:600;line-height:1;white-space:nowrap;">${label}</a>`;
        }).join("");
        slotsRow = `<tr><td style="padding:0;font-family:Arial,Helvetica,sans-serif;">`
            + buttons
            + `</td></tr>`;
    }

    const bodyCell = isWidget
        ? `font-family:Arial,Helvetica,sans-serif;font-size:15px;`
          + `font-weight:600;color:#0f172a;line-height:1.4;`
        : `font-family:Arial,Helvetica,sans-serif;font-size:15px;`
          + `font-weight:400;color:#111827;line-height:1.4;`;
    const footerCell = isWidget
        ? `font-family:Arial,Helvetica,sans-serif;font-size:13px;`
          + `font-weight:400;color:#64748b;line-height:1.4;`
        : bodyCell;
    // "buttons" frames the whole block with a light rule top and bottom so it
    // reads as a distinct section; "widget" has no extra rule (the card wrap
    // is the chrome); "links" has no border.
    const outerStyle = isWidget
        ? `margin:0;border-collapse:collapse;width:100%;`
        : style === "buttons"
          ? `margin:16px 0;border-collapse:collapse;border-top:1px solid #e5e7eb;`
            + `border-bottom:1px solid #e5e7eb;`
          : `margin:16px 0;border-collapse:collapse;`;
    const headingPad = isWidget ? "0 0 10px 0" : style === "buttons" ? "12px 0 8px 0" : "0 0 8px 0";
    const footerPad = isWidget ? "10px 0 0 0" : style === "buttons" ? "8px 0 12px 0" : "8px 0 0 0";

    const headingRow = omitHeading
        ? ""
        : `<tr><td style="padding:${headingPad};${bodyCell}">${escapeHtml(heading)}</td></tr>`;

    const footerMarkup = footerInner("color:#2563eb;");
    const footerRow = omitFooter || !footerMarkup
        ? ""
        : `<tr><td style="padding:${footerPad};${footerCell}">${footerMarkup}</td></tr>`;

    const table = `<table role="presentation" cellpadding="0" cellspacing="0" border="0" `
        + `style="${outerStyle}">`
        + headingRow
        + slotsRow
        + footerRow
        + `</table>`;

    // Card wrap only on the sent-email shape (heading + footer present). The
    // editor NodeView supplies its own matching preview chrome around the
    // editable heading/footer so we don't nest a half-empty card.
    if (isWidget && !omitHeading && !omitFooter) {
        return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" `
            + `style="margin:16px 0;border-collapse:separate;border-spacing:0;">`
            + `<tr><td bgcolor="#EEF3F8" style="background-color:#eef3f8;border:1px solid #d5dee8;`
            + `border-radius:12px;padding:18px 20px;">`
            + table
            + `</td></tr></table>`;
    }
    return table;
}
